import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Rocket, ShieldCheck, Ticket } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { useTurnstile } from "@/components/common/turnstile";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { authApi } from "@/lib/api/endpoints";
import { isApiError, isRecord } from "@/lib/api/errors";
import type { BillingInterval, PlanId } from "@/lib/api/types";
import { env } from "@/lib/env";
import { fromPence, pluralise } from "@/lib/format";
import { applyApiError } from "@/lib/form-errors";
import { planLabel } from "@/lib/plans";
import { cn } from "@/lib/utils";
import { usePlans } from "@/hooks/use-reference";
import { useAuth } from "@/providers/auth-provider";
import { AuthLayout } from "./auth-layout";

const PLAN_IDS = ["academy", "driver", "navigator", "oracle"] as const;

const schema = z.object({
  fullName: z.string().min(2, "Tell us your name"),
  businessName: z.string().min(2, "What's the business called?"),
  email: z.string().min(1, "Enter your email").email("That doesn't look like an email"),
  password: z.string().min(10, "Use at least 10 characters"),
  plan: z.enum(PLAN_IDS),
  inviteCode: z.string().optional(),
  marketingOptIn: z.boolean(),
  acceptTerms: z.boolean().refine((v) => v, { message: "Please accept the terms" }),
});

type Values = z.infer<typeof schema>;

function strengthOf(password: string) {
  let score = 0;
  if (password.length >= 10) score++;
  if (password.length >= 14) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^\w\s]/.test(password)) score++;
  return Math.min(4, score);
}

const STRENGTH_LABELS = ["Too short", "Weak", "Fair", "Good", "Strong"];

export default function SignupPage() {
  const { adoptSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const turnstile = useTurnstile("signup");
  const { plans, isPending: plansLoading, annualMonthsFree } = usePlans();

  const inviteFromUrl = params.get("invite") ?? "";
  const planFromUrl = params.get("plan");
  const [interval, setInterval] = React.useState<BillingInterval>("monthly");
  const [requiresInvite, setRequiresInvite] = React.useState(false);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: "",
      businessName: "",
      email: "",
      password: "",
      plan: (PLAN_IDS as readonly string[]).includes(planFromUrl ?? "")
        ? (planFromUrl as PlanId)
        : "driver",
      inviteCode: inviteFromUrl,
      marketingOptIn: false,
      acceptTerms: false,
    },
  });

  const plan = form.watch("plan");
  const password = form.watch("password");
  const strength = strengthOf(password ?? "");
  const showInviteField = Boolean(inviteFromUrl) || plan === "academy" || requiresInvite;

  const onSubmit = form.handleSubmit(async (values) => {
    form.clearErrors("root");
    if (turnstile.required && !turnstile.token) {
      form.setError("root", { message: "Please complete the security check." });
      return;
    }

    try {
      const session = await authApi.signup({
        email: values.email.trim(),
        password: values.password,
        fullName: values.fullName.trim(),
        businessName: values.businessName.trim(),
        plan: values.plan,
        inviteCode: values.inviteCode?.trim() || undefined,
        termsVersion: env.termsVersion,
        marketingOptIn: values.marketingOptIn,
        platform: "web",
        turnstileToken: turnstile.token ?? "",
      });
      await adoptSession(session);
      // requiresCheckout means billing is the next step, not the dashboard.
      navigate(session.requiresCheckout ? "/billing?welcome=1" : "/dashboard", { replace: true });
    } catch (error) {
      turnstile.reset();

      // Academy is invite-only: signing up without a code returns 403 with
      // details.requiresInvite.
      if (
        isApiError(error) &&
        error.code === "forbidden" &&
        isRecord(error.details) &&
        error.details.requiresInvite === true
      ) {
        setRequiresInvite(true);
        form.setError("inviteCode", {
          message: "Academy is invite-only — enter the code you were sent.",
        });
        return;
      }

      if (isApiError(error) && error.code === "email_taken") {
        form.setError("email", { message: "There's already an account with that email." });
        return;
      }

      applyApiError(error, form);
    }
  });

  const rootError = form.formState.errors.root?.message;

  return (
    <AuthLayout
      wide
      title="Create your account"
      subtitle="Fourteen fields once, then twelve numbers a month."
      footer={
        <>
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-6">
        {rootError && (
          <Alert variant="destructive">
            <ShieldCheck />
            <AlertDescription>{rootError}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Your name" htmlFor="fullName" required error={form.formState.errors.fullName?.message}>
            <Input id="fullName" autoComplete="name" autoFocus {...form.register("fullName")} />
          </Field>
          <Field
            label="Business name"
            htmlFor="businessName"
            required
            error={form.formState.errors.businessName?.message}
          >
            <Input id="businessName" autoComplete="organization" {...form.register("businessName")} />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email" htmlFor="email" required error={form.formState.errors.email?.message}>
            <Input id="email" type="email" autoComplete="email" {...form.register("email")} />
          </Field>
          <Field
            label="Password"
            htmlFor="password"
            required
            error={form.formState.errors.password?.message}
            hint="At least 10 characters."
          >
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              {...form.register("password")}
            />
            {password?.length > 0 && (
              <div className="mt-1 flex items-center gap-2">
                <div className="flex flex-1 gap-1" aria-hidden>
                  {[0, 1, 2, 3].map((i) => (
                    <span
                      key={i}
                      className={cn(
                        "h-1 flex-1 rounded-full transition-colors",
                        i < strength ? "bg-primary" : "bg-muted",
                      )}
                    />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">{STRENGTH_LABELS[strength]}</span>
              </div>
            )}
          </Field>
        </div>

        {/* Plan picker */}
        <fieldset className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <legend className="text-sm font-medium">Choose your plan</legend>
            <div className="inline-flex rounded-lg bg-muted p-0.5 text-xs font-medium">
              {(["monthly", "annual"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setInterval(value)}
                  aria-pressed={interval === value}
                  className={cn(
                    "rounded-md px-2.5 py-1 capitalize transition-colors",
                    interval === value ? "bg-card shadow-sm" : "text-muted-foreground",
                  )}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>

          {plansLoading ? (
            <div className="grid gap-3 sm:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-28 rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-3">
              {plans.map((p) => {
                const pence = interval === "annual" ? p.annualPence : p.monthlyPence;
                const selected = plan === p.id;
                return (
                  <label
                    key={p.id}
                    className={cn(
                      "relative cursor-pointer rounded-xl border p-3.5 transition-all",
                      selected
                        ? "border-primary bg-primary/6 ring-[3px] ring-ring/20"
                        : "hover:border-primary/40 hover:bg-accent/40",
                    )}
                  >
                    <input
                      type="radio"
                      value={p.id}
                      className="sr-only"
                      {...form.register("plan")}
                    />
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{p.label}</span>
                      {selected && <Check className="size-4 text-primary" />}
                    </div>
                    <p className="mt-1 text-lg font-semibold tracking-tight">
                      {fromPence(pence)}
                      <span className="text-xs font-normal text-muted-foreground">
                        /{interval === "annual" ? "yr" : "mo"}
                      </span>
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {pluralise(p.features?.length ?? 0, "feature")}
                    </p>
                    {interval === "annual" && annualMonthsFree > 0 && (
                      <Badge variant="brand" className="absolute -top-2 right-3">
                        {annualMonthsFree} months free
                      </Badge>
                    )}
                  </label>
                );
              })}
            </div>
          )}

          {/* Academy is deliberately absent from /reference/plans — the hidden tier */}
          {showInviteField && (
            <Alert variant="info">
              <Ticket />
              <AlertTitle>Invite code</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>
                  Academy is invite-only, and an invite pins the plan it was issued for — so the code
                  decides, not the choice above.
                </p>
                <Input
                  placeholder="ROCKET-XXXX-XXXX"
                  autoComplete="one-time-code"
                  aria-invalid={Boolean(form.formState.errors.inviteCode)}
                  {...form.register("inviteCode")}
                />
                {form.formState.errors.inviteCode?.message && (
                  <p className="text-xs font-medium text-destructive">
                    {form.formState.errors.inviteCode.message}
                  </p>
                )}
              </AlertDescription>
            </Alert>
          )}

          {!showInviteField && (
            <button
              type="button"
              onClick={() => setRequiresInvite(true)}
              className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
            >
              I have an invite code
            </button>
          )}
        </fieldset>

        <div className="space-y-3 rounded-xl border p-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="acceptTerms"
              checked={form.watch("acceptTerms")}
              onCheckedChange={(checked) =>
                form.setValue("acceptTerms", checked === true, { shouldValidate: true })
              }
              aria-invalid={Boolean(form.formState.errors.acceptTerms)}
            />
            <Label htmlFor="acceptTerms" className="text-sm leading-relaxed font-normal">
              I agree to the{" "}
              <a href="https://rocketeer.uk/terms" target="_blank" rel="noreferrer noopener" className="text-primary hover:underline">
                terms of service
              </a>{" "}
              and{" "}
              <a href="https://rocketeer.uk/privacy" target="_blank" rel="noreferrer noopener" className="text-primary hover:underline">
                privacy policy
              </a>
              .
            </Label>
          </div>
          {form.formState.errors.acceptTerms?.message && (
            <p className="text-xs font-medium text-destructive">
              {form.formState.errors.acceptTerms.message}
            </p>
          )}
          <div className="flex items-start gap-3">
            <Checkbox
              id="marketingOptIn"
              checked={form.watch("marketingOptIn")}
              onCheckedChange={(checked) => form.setValue("marketingOptIn", checked === true)}
            />
            <Label htmlFor="marketingOptIn" className="text-sm leading-relaxed font-normal">
              Send me occasional product news. No noise, unsubscribe any time.
            </Label>
          </div>
        </div>

        {turnstile.widget}

        <Button type="submit" size="lg" className="w-full" loading={form.formState.isSubmitting}>
          <Rocket className="size-4" />
          Create account &amp; start {planLabel(plan)}
        </Button>
      </form>
    </AuthLayout>
  );
}
